import * as routeNames from '@/router/routeNames';

import { ActionContext, Getter } from 'vuex';

import { AuthData } from '@/models/auth';
import HelperStorage from '@/helpers/HelperStorage';
import { State } from '@/models/main';
import jwtDecode from 'jwt-decode';
import router from '@/router';
import store from '@/store';
import { abortController, reinitController } from '@/utils/abortController';

export interface ModuleAuthDataState {
  authData: AuthData;
}

export const moduleAuthData = {
  namespaced: true,
  state: {
    authData: HelperStorage.get('auth_data', {
      refresh: '',
      access: '',
      permissions: [],
      exp: 0,
      // Think about moving this to user module (`/store/modules/user.ts`)
      customerCode: '',
      availableCustomerCodes: [],
    }),
  },
  getters: {
    isLoggedIn: (state: ModuleAuthDataState): boolean => !!state.authData.access,
    authData: (state: ModuleAuthDataState): ModuleAuthDataState['authData'] => state.authData,
    getAccessTokenExpTime: (state: ModuleAuthDataState): number => {
      const token = state.authData.access;
      if (token === '') {
        return 0;
      }
      const accessToken: AuthData = jwtDecode(token);
      return accessToken['exp'];
    },
    getRefreshTokenExpTime: (state: ModuleAuthDataState): number => {
      const token = state.authData.refresh;
      if (token === '') {
        return 0;
      }
      const refreshToken: AuthData = jwtDecode(token);
      return refreshToken['exp'];
    },
    //TODO: is there a way to rewrite these two function in arrow function style?
    //  Something like that: isAccessTokenValid: (state: ModuleAuthDataState) =>
    //  (now: number): boolean => { const exp: number = state.authData.exp; return exp > now; },
    isRefreshTokenValid(state: ModuleAuthDataState, getters: Getter<ModuleAuthDataState, State>) {
      return (now: number): boolean => {
        const exp: number = getters['getRefreshTokenExpTime'];
        return exp > now;
      };
    },
    isAccessTokenValid(state: ModuleAuthDataState, getters: Getter<ModuleAuthDataState, State>) {
      return (now: number): boolean => {
        const exp: number = getters['getAccessTokenExpTime'];
        return exp > now;
      };
    },
  },
  actions: {
    async addAuthData({ commit }: ActionContext<ModuleAuthDataState, State>, data: AuthData): Promise<void> {
      commit('ADD_AUTH_DATA', data);
    },
    async removeAuthData({ commit }: ActionContext<ModuleAuthDataState, State>): Promise<void> {
      commit('REMOVE_AUTH_DATA');
    },
    // This is the action that is called when the user clicks the logout button
    async logOutUser({ commit }: ActionContext<ModuleAuthDataState, State>): Promise<void> {
      commit('REMOVE_AUTH_DATA');
      // We need to abort all requests that are currently in progress because the user is logged out
      abortController();
      // Abort controller should be reinitialized after the logout because previous instance is aborted
      reinitController();
      store.commit('moduleUser/REMOVE_USER_DATA');
      store.commit('CLEAR_JOB_DATA');
      store.commit('moduleStaticData/CLEAR_STATIC_STATE');
    },
    // This is the action that is called when the user's tokens have expired
    async forceRenewRefreshToken({ commit }: ActionContext<ModuleAuthDataState, State>): Promise<void> {
      commit('REMOVE_AUTH_DATA');
      // Abort controller should be reinitialized after the logout because previous instance is aborted
      reinitController();
      if (router.currentRoute.name !== routeNames.LOGIN) {
        await router.push({
          path: '/login',
        });
        await store.dispatch('setLoginError', 'The session has expired');
      }
    },
    async updateAccessToken({ commit }: ActionContext<ModuleAuthDataState, State>, data: string): Promise<void> {
      commit('UPDATE_ACCESS_TOKEN', data);
    },
  },
  mutations: {
    ADD_AUTH_DATA(state: ModuleAuthDataState, data: AuthData): void {
      state.authData = data;
      HelperStorage.set('auth_data', data);
    },
    REMOVE_AUTH_DATA(state: ModuleAuthDataState): void {
      const authData: AuthData = JSON.parse(
        JSON.stringify({
          access: '',
          refresh: '',
          permissions: [],
          exp: 0,
          customerCode: '',
          availableCustomerCodes: [],
        }),
      );
      state.authData = authData;
      HelperStorage.set('auth_data', authData);
    },
    // updates access token from the refresh token request
    UPDATE_ACCESS_TOKEN(state: ModuleAuthDataState, data: string): void {
      state.authData.access = data;
      HelperStorage.set('auth_data', state.authData);
    },
  },
};
